import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router';
import React, { useState, useEffect } from 'react';
import { m, AnimatePresence } from 'framer-motion';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller, FormProvider, useFieldArray } from 'react-hook-form';

import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import { Add, Save, Delete, ContentCopy } from '@mui/icons-material';
import {
  Box,
  Card,
  Grid,
  Stack,
  Button,
  Dialog,
  Tooltip,
  MenuItem,
  TextField,
  IconButton,
  Typography,
  DialogContent,
  DialogActions,
  InputAdornment,
} from '@mui/material';

import { paths } from 'src/routes/routes/paths';

import {
  createRuleTemplate,
  updateRuleTemplate,
  getAllRuleTemplateList,
  getRuleTemplateDetails,
  resetRuleTemplateCreated,
} from 'src/server/store/automation';

import SaveRuleTemplateModal, { CriteriaSummary } from './SaveRuleTemplateModal';

// --- Constants (from README) ---
const METRIC_OPTIONS = [
  { label: 'ACOS', value: 'ACOS', type: 'percentage' },
  { label: 'ROAS', value: 'ROAS', type: 'count' },
  { label: 'Clicks', value: 'Clicks', type: 'count' },
  { label: 'Current Bid', value: 'Current Bid', type: 'price' },
  { label: 'Impressions', value: 'Impressions', type: 'count' },
  { label: 'CVR', value: 'CVR', type: 'percentage' },
  { label: 'CTR', value: 'CTR', type: 'percentage' },
  { label: 'CPC', value: 'CPC', type: 'price' },
  { label: 'PPC Orders', value: 'PPC Orders', type: 'count' },
  { label: 'Spend', value: 'Spend', type: 'price' },
  { label: 'Sales', value: 'Sales', type: 'price' },
];
const OPERATOR_OPTIONS = [
  { label: 'Greater than >', value: 'GREATER_THAN' },
  { label: 'Greater than or equal >=', value: 'GREATER_THAN_OR_EQUAL' },
  { label: 'Less than <', value: 'LESS_THAN' },
  { label: 'Less than or equal <=', value: 'LESS_THAN_OR_EQUAL' },
  { label: 'Equal =', value: 'EQUAL' },
];

const ACTION_TYPE_OPTIONS = [
  { label: 'Set Bid', value: 'SET_BID', type: 'price' },
  { label: 'Increase Bid Amount', value: 'INCREASE_BID_AMOUNT', type: 'price' },
  { label: 'Increase Bid Percentage', value: 'INCREASE_BID_PERCENTAGE', type: 'percentage' },
  { label: 'Decrease Bid Amount', value: 'DECREASE_BID_AMOUNT', type: 'price' },
  { label: 'Decrease Bid Percentage', value: 'DECREASE_BID_PERCENTAGE', type: 'percentage' },
  { label: 'Set to CPC', value: 'SET_TO_CPC', type: 'price' },
  { label: 'CPC × Target ACOS / ACOS', value: 'CPC_ACOS', type: 'price' },
  {
    label: 'Current Bid × Target ACOS / ACOS',
    value: 'BID_ACOS',
    type: 'price',
  },
  { label: 'Enable Targeting', value: 'ENABLE_TARGETING', type: 'targeting' },
  { label: 'Pause Targeting', value: 'PAUSE_TARGETING', type: 'targeting' },
];

const LOOKBACK_OPTIONS = [
  { label: 'Last 3 Days', value: 'LAST_3_DAYS', title: 'Last 3 Days' },
  { label: 'Last 7 Days', value: 'LAST_7_DAYS', title: 'Last 7 Days' },
  { label: 'Last 14 Days', value: 'LAST_14_DAYS', title: 'Last 14 Days' },
  { label: 'Last 30 Days', value: 'LAST_30_DAYS', title: 'Last 30 Days' },
  { label: 'Last 60 Days', value: 'LAST_60_DAYS', title: 'Last 60 Days' },
];
const EXCLUDE_OPTIONS = [
  { label: 'None', value: '' },
  { label: 'Last 3 Days', value: 'LAST_3_DAYS', title: 'Last 3 Days' },
  { label: 'Last 7 Days', value: 'LAST_7_DAYS', title: 'Last 7 Days' },
];

// --- Validation Schema ---
const conditionSchema = Yup.object().shape({
  metric: Yup.string().required('Metric is required'),
  operator: Yup.string().required('Operator is required'),
  value: Yup.number().typeError('Value must be a number').required('Value is required').min(0),
});

const safetyLimitsSchema = Yup.object().shape({
  minBid: Yup.number()
    .typeError('Min Bid must be a number')
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  maxBid: Yup.number()
    .typeError('Max Bid must be a number')
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  // minBudget: Yup.number().typeError('Min Budget must be a number'),
  // maxBudget: Yup.number().typeError('Max Budget must be a number'),
});

const criteriaSchema = Yup.object().shape({
  conditions: Yup.array().of(conditionSchema).min(1, 'At least one condition'),
  action: Yup.object().shape({
    actionType: Yup.string().required('Action type is required'),
    value: Yup.string().required('Action value is required').min(0),
    safetyLimits: safetyLimitsSchema,
  }),
  lookbackPeriod: Yup.string().required('Lookback period is required'),
  exclude: Yup.string(),
});

const schema = Yup.object().shape({
  title: Yup.string().required('Title is required'),
  description: Yup.string().max(150),
  criteria: Yup.array().of(criteriaSchema).min(1, 'At least one criteria'),
});

function generateUniqueId() {
  return `${Date.now()}_${Math.floor(Math.random() * 1000000)}`;
}

const createDefaultCriteria = () => ({
  _id: generateUniqueId(),
  conditions: [{ metric: '', operator: '', value: '' }],
  action: {
    actionType: '',
    value: '',
    safetyLimits: {
      minBid: null,
      maxBid: null,
      // minBudget: 50,
      // maxBudget: 1000,
    },
  },
  lookbackPeriod: 'LAST_7_DAYS',
  exclude: 'LAST_3_DAYS',
});

const defaultValues = {
  title: '',
  description: '',
  criteria: [createDefaultCriteria()],
};

AutomationRuleCreate.propTypes = {
  type: PropTypes.string,
  ruleTemplateId: PropTypes.string,
  setFinalRuleTemplateId: PropTypes.func,
  openRuleModal: PropTypes.bool,
  setOpenRuleModal: PropTypes.func,
};

export default function AutomationRuleCreate({
  type = 'full',
  ruleTemplateId,
  setFinalRuleTemplateId,
  openRuleModal,
  setOpenRuleModal,
}) {
  const [openSaveModal, setOpenSaveModal] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [, setCriteriaDirty] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { allRuleTemplateList, loading, ruleTemplateDetails, ruleTemplateCreated } = useSelector(
    (state) => ({
      allRuleTemplateList: state?.automation?.allRuleTemplateList,
      loading: state?.automation?.loading,
      ruleTemplateDetails: state?.automation?.ruleTemplateDetails,
      ruleTemplateCreated: state?.automation?.ruleTemplateCreated,
    })
  );

  useEffect(() => {
    if (ruleTemplateId) {
      dispatch(getRuleTemplateDetails({ credentials: { id: ruleTemplateId } }));
    }
  }, [ruleTemplateId, dispatch]);

  useEffect(() => {
    if (type === 'minimal') {
      dispatch(getAllRuleTemplateList({ credentials: {} }));
    }
  }, [type, dispatch]);

  const ruleOptions = allRuleTemplateList?.automationRuleTemplates || [];

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = methods;
  const { fields: criteriaFields, append } = useFieldArray({
    control,
    name: 'criteria',
  });

  console.log(errors);

  useEffect(() => {
    if (selectedRule && selectedRule.criteria) {
      setValue('criteria', selectedRule.criteria);
    }
  }, [selectedRule, setValue]);

  // Populate form with existing data when editing
  useEffect(() => {
    if (ruleTemplateDetails && ruleTemplateDetails._id && ruleTemplateId) {
      const { title, description, criteria } = ruleTemplateDetails;

      // Set form values
      setValue('title', title || '');
      setValue('description', description || '');

      // Transform criteria to match form structure
      if (criteria && Array.isArray(criteria)) {
        const transformedCriteria = criteria.map((criterion) => ({
          _id: criterion._id, // Preserve existing _id
          conditions: criterion.conditions?.map((condition) => ({
            metric: condition.metric || '',
            operator: condition.operator || '',
            value: condition.value || '',
          })) || [{ metric: '', operator: '', value: '' }],
          action: {
            actionType: criterion.action?.actionType || '',
            value: criterion.action?.value || '',
            safetyLimits: {
              minBid: Number(criterion.action?.safetyLimits?.minBid) || null,
              maxBid: Number(criterion.action?.safetyLimits?.maxBid) || null,
              // minBudget: Number(criterion.action?.safetyLimits?.minBudget) || 50,
              // maxBudget: Number(criterion.action?.safetyLimits?.maxBudget) || 1000,
            },
          },
          lookbackPeriod: criterion.lookbackPeriod || 'LAST_7_DAYS',
          exclude: criterion.exclude || 'LAST_3_DAYS',
        }));

        setValue('criteria', transformedCriteria);
      }
    }
  }, [ruleTemplateDetails, ruleTemplateId, setValue]);

  const handleCloseModal = () => {
    setOpenSaveModal(false);
    setOpenRuleModal(false);
  };

  const [ruleConfirmation, setRuleConfirmation] = useState({
    status: false,
    data: null,
  });

  const onSubmit = (data) => {
    console.log('ameen', data);

    // Format the data according to the new API structure
    const formattedData = {
      ...data,
      criteria: data.criteria.map((criterion) => ({
        action: criterion?.action,
        lookbackPeriod: criterion.lookbackPeriod,
        exclude: criterion.exclude,
        conditions: criterion?.conditions,
      })),
    };

    if (ruleTemplateId) {
      setRuleConfirmation({ status: true, data: formattedData });
    } else {
      // Create new rule template
      dispatch(
        createRuleTemplate({
          credentials: {
            body: formattedData,
            dispatch,
            setFinalRuleTemplateId,
            handleCloseModal: handleCloseRuleConfirmationModal,
            redirect: openSaveModal
              ? () => {}
              : () => navigate(`${paths.dashboard.automation}?sort=RULE`),
          },
        })
      );
    }
  };

  const handleCloseRuleConfirmationModal = () => {
    setRuleConfirmation({ status: false, data: null });
  };

  const handleUpdateRuleTemplate = () => {
    // Update existing rule template
    dispatch(
      updateRuleTemplate({
        credentials: {
          id: ruleTemplateId,
          body: ruleConfirmation?.data,
          dispatch,
          handleCloseModal: handleCloseRuleConfirmationModal,
        },
      })
    );
  };

  const watchedCriteria = watch('criteria');

  useEffect(() => {
    if (
      selectedRule &&
      watchedCriteria &&
      JSON.stringify(watchedCriteria) !== JSON.stringify(selectedRule.criteria)
    ) {
      setCriteriaDirty(true);
    }
  }, [watchedCriteria, selectedRule]);

  const criteriaList = watch('criteria');

  const handleDeleteCriteria = (id) => {
    const newCriteria = criteriaList.filter((criteria) => criteria._id !== id);
    setValue('criteria', newCriteria);
  };

  useEffect(() => {
    dispatch(resetRuleTemplateCreated());
  }, [criteriaList, dispatch]);

  const checkOneItemPresent = !!criteriaList?.[0]?.conditions?.[0]?.metric;
  console.log(checkOneItemPresent);

  return (
    <>
      <Dialog
        open={ruleConfirmation.status}
        onClose={handleCloseRuleConfirmationModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="subtitle2">Confirm Rule Template Changes</Typography>
          <Stack spacing={2} mt={2}>
            <Typography variant="body2">
              Are you sure you want to update the rule template? This change will take effect in the
              next scheduled run
            </Typography>
            <CriteriaSummary criteriaList={ruleConfirmation?.data?.criteria || []} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRuleConfirmationModal}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            color="success"
            onClick={handleUpdateRuleTemplate}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {type === 'full' && (
            <Grid container xs={12} spacing={2} mb={3} sx={{ position: 'relative' }}>
              <Grid item xs={6} spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Rule Template
                  </Typography>
                </Grid>
                <Grid item xs={12} mb={2}>
                  <Controller
                    name="title"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Title"
                        fullWidth
                        error={!!errors.title}
                        helperText={errors.title?.message}
                        size="small"
                        required
                        InputLabelProps={{ required: true }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} mb={2}>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Description"
                        fullWidth
                        error={!!errors.description}
                        helperText={errors.description?.message}
                        size="small"
                        multiline
                        rows={4}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              <Grid item xs={6}>
                <Box
                  sx={{
                    position: 'fixed',
                    top: 100,
                    right: 40,
                    zIndex: 10,
                    background: 'transparent',
                    py: 2,
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Tooltip
                    title="Before establishing a rule, make sure the criteria is accurate, and verify it again."
                    placement="bottom"
                  >
                    <Button
                      type="submit"
                      color="success"
                      variant="contained"
                      size="medium"
                      disabled={!criteriaList || criteriaList.length === 0}
                    >
                      {ruleTemplateId ? 'Update Rule' : 'Create Rule'}
                    </Button>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          )}

          <Stack mb={2} direction="row" justifyContent="space-between">
            <Stack>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Criteria
                <Typography variant="caption" sx={{ mb: 2, color: 'text.secondary' }}>
                  &nbsp;: Set up the performance criteria and actions
                </Typography>
              </Typography>
            </Stack>

            {/* // only type === 'minimal'  thens select already created rules, in dropdown */}
            {type === 'minimal' && (
              <Autocomplete
                options={ruleOptions}
                getOptionLabel={(option) => option.title || ''}
                value={selectedRule}
                onChange={(event, newValue) => {
                  if (!newValue) {
                    setSelectedRule(null);
                    setCriteriaDirty(false);
                    setFinalRuleTemplateId(null);
                  } else {
                    setSelectedRule(newValue);
                    setCriteriaDirty(false);
                    setFinalRuleTemplateId(newValue?._id);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select rule template"
                    variant="outlined"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      minWidth: 300,
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#66BB6A',
                          borderWidth: 2,
                        },
                        '&:hover fieldset': {
                          borderColor: '#43A047',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#388E3C',
                          boxShadow: '0 0 0 2px #66BB6A55',
                        },
                      },
                    }}
                  />
                )}
                sx={{ minWidth: 300 }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                loading={loading}
              />
            )}
          </Stack>

          {errors.criteria && (
            <Typography color="error" variant="caption" sx={{ ml: 1, mb: 1 }}>
              {errors.criteria.message}
            </Typography>
          )}
          <AnimatePresence>
            {criteriaFields.map((criteria, cIdx) => {
              const condName = `criteria[${cIdx}].conditions`;
              const actionName = `criteria[${cIdx}].action`;
              const lookbackName = `criteria[${cIdx}].lookbackPeriod`;
              return (
                <m.div
                  key={criteria._id}
                  animate={{ opacity: 1, x: 0, y: 0 }}
                  exit={{ opacity: 0, x: -100, transition: { duration: 0.5 } }}
                  style={{ width: '100%' }}
                >
                  <Card key={criteria._id} sx={{ mb: 3, p: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="subtitle2">Criteria {cIdx + 1}</Typography>
                      <Box>
                        {ruleTemplateId && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              const clonedCriteria = {
                                ...JSON.parse(JSON.stringify(criteria)),
                                _id: generateUniqueId(),
                              };
                              append(clonedCriteria);
                            }}
                          >
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        )}
                        {criteriaFields.length > 1 && (
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteCriteria(criteria._id)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Stack>
                    <Box sx={{ mt: 1 }}>
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        {/* Left: IF/THEN with animated line */}
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            minWidth: 40,
                          }}
                        >
                          <Box
                            sx={{
                              bgcolor: '#F5EFFF',
                              color: '#A259FF',
                              px: 2,
                              py: 0.3,
                              borderRadius: 2,
                              fontWeight: 600,
                              fontSize: 12,
                              mb: 0.5,
                            }}
                          >
                            IF
                          </Box>
                          {/* Animated vertical line */}
                          <AnimatedCriteriaLine numConditions={watch(condName)?.length || 1} />
                          <Box
                            sx={{
                              bgcolor: '#E6F0FF',
                              color: '#2D8CFF',
                              px: 2,
                              py: 0.3,
                              borderRadius: 2,
                              fontWeight: 600,
                              fontSize: 12,
                              mt: 0.5,
                            }}
                          >
                            THEN
                          </Box>
                        </Box>
                        {/* Right: Conditions and Actions */}
                        <Box sx={{ flex: 1 }}>
                          <ConditionList nestName={condName} control={control} watch={watch} />
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>
                            &nbsp;
                          </Typography>
                          <ActionSection nestName={actionName} control={control} watch={watch} />
                        </Box>
                      </Stack>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" mb={1}>
                          Lookback period *
                        </Typography>
                        <Stack direction="row" spacing={2} mt={2} alignItems="center">
                          <Controller
                            name={`${lookbackName}`}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                select
                                label=""
                                {...field}
                                error={!!errors?.criteria?.[cIdx]?.lookbackPeriod}
                                helperText={errors?.criteria?.[cIdx]?.lookbackPeriod?.message}
                                sx={{ minWidth: 140 }}
                                size="small"
                              >
                                {LOOKBACK_OPTIONS.map((opt) => (
                                  <MenuItem key={opt.value} value={opt.value}>
                                    {opt.title}
                                  </MenuItem>
                                ))}
                              </TextField>
                            )}
                          />
                          <Typography variant="caption" mb={0}>
                            Exclude
                          </Typography>
                          <Controller
                            name={`criteria[${cIdx}].exclude`}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                select
                                label=""
                                {...field}
                                error={!!errors?.criteria?.[cIdx]?.exclude}
                                helperText={errors?.criteria?.[cIdx]?.exclude?.message}
                                sx={{ minWidth: 140 }}
                                size="small"
                              >
                                {EXCLUDE_OPTIONS.map((opt) => (
                                  <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                            )}
                          />
                        </Stack>
                      </Box>
                    </Box>
                  </Card>
                </m.div>
              );
            })}
          </AnimatePresence>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => append(createDefaultCriteria())}
            >
              Criteria
            </Button>
            {type === 'minimal' && !ruleTemplateCreated && (
              <Stack direction="row" justifyContent="end">
                <Button
                  sx={{ minWidth: 180 }}
                  size="small"
                  variant="outlined"
                  startIcon={<Save fontSize="small" />}
                  onClick={() => setOpenSaveModal(true)}
                  disabled={!checkOneItemPresent}
                >
                  Save Rule Template
                </Button>
              </Stack>
            )}
          </Stack>
        </form>
      </FormProvider>
      <SaveRuleTemplateModal
        open={openSaveModal || openRuleModal}
        onClose={handleCloseModal}
        onSubmit={(modalData) => {
          setValue('title', modalData.title);
          setValue('description', modalData.description);
          setValue('criteria', modalData.criteria);
          handleSubmit(onSubmit)();
        }}
        criteriaList={criteriaList}
        initialTitle={watch('title')}
        initialDescription={watch('description')}
      />
    </>
  );
}

ConditionList.propTypes = {
  nestName: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  watch: PropTypes.func.isRequired,
};

function ConditionList({ nestName, control, watch }) {
  const { fields, append, remove } = useFieldArray({ control, name: nestName });
  return (
    <>
      {fields.map((condField, idx) => (
        <>
          <Stack direction="row" spacing={2} alignItems="center" key={condField.id} sx={{ mb: 1 }}>
            {idx > 0 && (
              <Box
                sx={{
                  bgcolor: '#e8ecee',
                  color: '#555',
                  px: 1,
                  py: 0.3,
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: 11,
                  // mb: 0.5,
                  mt: 0.5,
                }}
              >
                AND
              </Box>
            )}
            <Controller
              name={`${nestName}[${idx}].metric`}
              control={control}
              render={({ field: metricField }) => (
                <TextField
                  select
                  label="Metric"
                  {...metricField}
                  sx={{ minWidth: idx === 0 ? 276 : 220 }}
                  size="small"
                >
                  {METRIC_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name={`${nestName}[${idx}].operator`}
              control={control}
              render={({ field: operatorField }) => (
                <TextField
                  select
                  label="Operator"
                  {...operatorField}
                  sx={{ minWidth: 220 }}
                  size="small"
                >
                  {OPERATOR_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name={`${nestName}[${idx}].value`}
              control={control}
              render={({ field: valueField }) => {
                const selectedMetric = METRIC_OPTIONS.find(
                  (opt) => opt.value === watch(`${nestName}[${idx}].metric`)
                );
                const metricType = selectedMetric?.type;
                let startAdornment = null;
                let endAdornment = null;
                if (metricType === 'percentage') {
                  endAdornment = <InputAdornment position="end">%</InputAdornment>;
                } else if (metricType === 'price') {
                  startAdornment = <InputAdornment position="start">₹</InputAdornment>;
                }
                return (
                  <TextField
                    label="Value"
                    {...valueField}
                    sx={{ minWidth: 100 }}
                    InputProps={{ startAdornment, endAdornment }}
                    size="small"
                    type="number"
                    required
                    InputLabelProps={{ required: true }}
                    inputProps={{ min: 0 }}
                  />
                );
              }}
            />
            <IconButton
              onClick={() => remove(idx)}
              disabled={fields.length === 1}
              sx={{ width: '30px', height: '30px' }}
            >
              <span style={{ fontSize: '1em', lineHeight: 1 }}>&times;</span>
            </IconButton>
          </Stack>
          {idx === fields.length - 1 && (
            <Button
              size="small"
              color="success"
              variant="text"
              onClick={() => append({ metric: '', operator: '', value: '' })}
            >
              <span
                style={{ fontSize: 18, marginRight: 4, display: 'inline-block', lineHeight: 1 }}
              >
                +
              </span>
              AND
            </Button>
          )}
        </>
      ))}
    </>
  );
}

ActionSection.propTypes = {
  nestName: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  watch: PropTypes.func.isRequired,
};

function ActionSection({ nestName, control, watch }) {
  const selectedAction = ACTION_TYPE_OPTIONS.find(
    (opt) => opt.value === watch(`${nestName}.actionType`)
  );
  const actionType = selectedAction?.type;

  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Controller
          name={`${nestName}.actionType`}
          control={control}
          render={({ field }) => (
            <TextField select label="Action" {...field} sx={{ minWidth: 220 }} size="small">
              {ACTION_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        <Controller
          name={`${nestName}.value`}
          control={control}
          render={({ field }) => {
            let startAdornment;
            let endAdornment;
            if (actionType === 'percentage') {
              endAdornment = <InputAdornment position="end">%</InputAdornment>;
            } else if (actionType === 'price') {
              startAdornment = <InputAdornment position="start">₹</InputAdornment>;
            }

            return (
              // if actionType is targeting then disabled
              <Tooltip title={actionType === 'targeting' ? 'Disabled' : ''} arrow>
                <TextField
                  label="Value"
                  {...field}
                  sx={{ minWidth: 220 }}
                  InputProps={{ startAdornment, endAdornment }}
                  size="small"
                  type="number"
                  disabled={actionType === 'targeting'}
                  required={actionType !== 'targeting'}
                  InputLabelProps={{ required: actionType !== 'targeting' }}
                  inputProps={{ min: 0 }}
                />
              </Tooltip>
            );
          }}
        />
      </Stack>

      {actionType === 'percentage' && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
            Safety Limits
          </Typography>

          <Stack direction="row" spacing={2} alignItems="center">
            <Controller
              name={`${nestName}.safetyLimits.minBid`}
              control={control}
              render={({ field }) => (
                <TextField
                  label="Min Bid"
                  type="number"
                  {...field}
                  sx={{ minWidth: 120 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                  size="small"
                />
              )}
            />
            <Controller
              name={`${nestName}.safetyLimits.maxBid`}
              control={control}
              render={({ field }) => (
                <TextField
                  label="Max Bid"
                  type="number"
                  {...field}
                  sx={{ minWidth: 120 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                  size="small"
                />
              )}
            />

            {/* <Controller
            name={`${nestName}.safetyLimits.minBudget`}
            control={control}
            render={({ field }) => (
              <TextField
                label="Min Budget"
                type="number"
                {...field}
                sx={{ minWidth: 120 }}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                size="small"
              />
            )}
          />
          <Controller
            name={`${nestName}.safetyLimits.maxBudget`}
            control={control}
            render={({ field }) => (
              <TextField
                label="Max Budget"
                type="number"
                {...field}
                sx={{ minWidth: 120 }}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                size="small"
              />
            )}
          /> */}
          </Stack>
        </Box>
      )}
    </Stack>
  );
}

const LINE_HEIGHT_PER_CONDITION = 46; // px per condition row (adjust as needed)
const LINE_MIN_HEIGHT = 85; // minimum height for 1 condition

function AnimatedCriteriaLine({ numConditions }) {
  const targetHeight = Math.max(
    LINE_MIN_HEIGHT,
    (numConditions - 1) * LINE_HEIGHT_PER_CONDITION + LINE_MIN_HEIGHT
  );
  return (
    <m.div
      initial={{ height: LINE_MIN_HEIGHT }}
      animate={{ height: targetHeight }}
      transition={{ type: 'spring', stiffness: 120, damping: 18 }}
      style={{
        borderLeft: '2px dashed #D3D3D3',
        width: 0,
        margin: '8px 0',
      }}
    />
  );
}

AnimatedCriteriaLine.propTypes = {
  numConditions: PropTypes.number.isRequired,
};

// --- Auto Saving Effect ---
// useEffect(() => {
//   if (autoSaving?.isSavingInStarted) {
//     // Generate title and description
//     const criteria = watch('criteria');
//     const title = `Rule Template - ${new Date().toLocaleString()}`;
//     const description = `Auto-saved rule template on ${new Date().toLocaleString()}`;
//     const formattedData = {
//       title,
//       description,
//       criteria: criteria.map((criterion) => ({
//         ...criterion,
//         lookbackPeriod: criterion.lookbackPeriod,
//       })),
//     };

//     console.log(formattedData);

//     setTimeout(() => {
//       setAutoSaving({
//         isSavingInStarted: false,
//         isSavingInCompleted: true,
//         isSavingInFailed: false,
//         message: 'Rule template saved successfully',
//       });
//     }, 5000);

//     // // Call API
//     // dispatch(
//     //   createRuleTemplate({
//     //     credentials: {
//     //       body: formattedData,
//     //       dispatch,
//     //       setFinalRuleTemplateId,
//     //     },
//     //   })
//     // )
//     //   .unwrap()
//     //   .then(() => {
//     //     setAutoSaving({
//     //       isSavingInStarted: false,
//     //       isSavingInCompleted: true,
//     //       isSavingInFailed: false,
//     //       message: 'Rule template saved',
//     //     });
//     //     console.log('ameen', 'success');
//     //   })
//     //   .catch(() => {
//     //     setAutoSaving({
//     //       isSavingInStarted: false,
//     //       isSavingInCompleted: false,
//     //       isSavingInFailed: true,
//     //       message: 'Failed to save rule template',
//     //     });
//     //     console.log('ameen', 'error');
//     //   });
//   }
//   // eslint-disable-next-line react-hooks/exhaustive-deps
// }, [autoSaving?.isSavingInStarted]);
